import { useCallback, useMemo, useState } from 'react';
import { isEqual, isNull, mapValues, pick } from 'lodash-es';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faArrows, faChartLine } from '@fortawesome/free-solid-svg-icons';

import { distinctUntilChanged, map, skipWhile, switchMap } from 'rxjs';
import { useObservable, useObservableState } from 'observable-hooks';
import { useQuery } from '@apollo/client';
import type { AlarmLine } from '@/graphql/types';
import { useProfile } from 'assets/js/profile';
import classNames from '../../../utils/classNames';
import { convertUnits, distanceConverter, roundToN } from '../../../utils/formatting';

import type { Alarm } from './AlarmLinesGraph';
import { AlarmLinesGraph, createEmptyAlarmPoints } from './AlarmLinesGraph';
import { useSharedFormContext, useWatchObservable } from '../../reactForm/utils';
import { LayoutHelper } from '../../reactForm/layout-helper';
import { RoundedButton } from '../../form/Button';

import './alarm-preview-graph.scss';
import { alarmLineQuery } from '../../../graph/queries';
import LoadingSpinner from '../../loadingSpinner/LoadingSpinner';
import { SwarmType } from '../../../enums';
import { useFormSchema } from './schema-utils';
import { switchHead } from '../../../utils/rxjs';
import type { TranslatedFormSchema } from './types';
import { FloatingPanelTitle } from './FloatingPanelTitle';

// Type for measurement settings (key-value pairs of form values)
type MeasurementSettings = Record<string, unknown> & {
    selfDefinedAlarmLine?: string | number;
};

// Type for notification config with max alarms
interface NotificationConfig {
    maxAlarms: Array<{ alarmLevel: number }>;
}

function initializeAlarm(alarmLevelIndex?: number): Alarm {
    return {
        points: createEmptyAlarmPoints(),
        alarmLevelIndex,
    };
}

const EMPTY_CATEGORY_ALARM: Alarm = initializeAlarm();

function useMeasurementSettings(): MeasurementSettings | null {
    const { formController } = useSharedFormContext();
    const profile = useProfile();

    const formSchema = useFormSchema(SwarmType.Vibration, false);
    const measurementSettingsFields = useMemo(() => {
        const layoutHelper = new LayoutHelper(
            formSchema.layout as unknown as TranslatedFormSchema['layout'],
            formSchema.fields as unknown as TranslatedFormSchema['fields']
        );
        return layoutHelper.getFieldsStartingAt('guideLine');
    }, [formSchema]);

    const measurementSettings$ = useObservable(
        (inputs$) =>
            inputs$.pipe(
                switchMap(([incomingValues$, incomingMeasurementSettings]) =>
                    incomingValues$.pipe(
                        map((values) =>
                            mapValues(
                                pick(values, Object.keys(incomingMeasurementSettings)),
                                // Convert the values to the units defined in the form schema.
                                (value, key) => {
                                    const fieldDefinition = incomingMeasurementSettings[key];

                                    if (!fieldDefinition) {
                                        return value as unknown;
                                    }

                                    return convertUnits(fieldDefinition, value, profile, true);
                                }
                            )
                        ),
                        distinctUntilChanged(isEqual)
                    )
                )
            ),
        [formController.finalStableValues$, measurementSettingsFields]
    );

    return useObservableState(measurementSettings$, null) as MeasurementSettings | null;
}

function useCategoryWatcher(): [Alarm, boolean] {
    const measurementSettings = useMeasurementSettings();
    const profile = useProfile();

    const { data, error, loading } = useQuery<{ alarmLine: AlarmLine }>(alarmLineQuery, {
        variables: {
            config: JSON.stringify({}),
            override: JSON.stringify({
                ...measurementSettings,
                // Ensure `selfDefinedAlarmLine` is always passed as an integer,
                // since it may come as a string from the `ChoiceField` component.
                selfDefinedAlarmLine: parseInt(
                    String(measurementSettings?.selfDefinedAlarmLine ?? 0),
                    10
                ),
            }),
        },
        skip: !measurementSettings,
    });

    const alarm = useMemo(() => {
        // `line` can be null in case of SBR-B, DIN4150-2 and
        // configs with an unavailable self defined line.
        if (error || !data?.alarmLine?.line) {
            return EMPTY_CATEGORY_ALARM;
        }

        return {
            alarmLevelIndex: null,
            points: data.alarmLine.line.map((line) => ({
                freq: line!.freq,
                vkar: line!.vkar
                    ? roundToN(distanceConverter(line!.vkar, profile.distanceUnit))
                    : null,
            })),
        };
    }, [data, error, profile.distanceUnit]);

    return [alarm, loading];
}

function useAlarmLevelsWatcher(): number[] {
    const notificationConfig$ = useWatchObservable<NotificationConfig>(`notificationConfig`);

    const alarmLevels$ = useObservable(
        (inputs$) =>
            inputs$.pipe(
                switchHead(),
                map((config) => config.maxAlarms.map((alarm) => alarm.alarmLevel)),
                // Skip the first emission if it's an empty array.
                // Since the initial state of alarm levels is already set as an empty
                // array, re-emitting an empty array on the first emission is redundant
                // and unnecessarily triggers updates.
                skipWhile((alarmLevels, index) => index === 0 && alarmLevels.length === 0)
            ),
        [notificationConfig$]
    );

    return useObservableState(alarmLevels$, []);
}

interface AlarmPreviewGraphPanelProps {
    onClose?: () => void;
}

export function AlarmPreviewGraphPanel({ onClose }: AlarmPreviewGraphPanelProps): JSX.Element {
    const [isExpanded, setIsExpanded] = useState(false);
    const [categoryAlarm, loading] = useCategoryWatcher();
    const alarmLevels = useAlarmLevelsWatcher();

    const alarms: Alarm[] = useMemo(
        () => [
            categoryAlarm,
            ...alarmLevels.map((alarmLevel, index) => {
                const percentageLevel = 100 - alarmLevel;

                return {
                    alarmLevelIndex: index,
                    points: categoryAlarm.points.map((point) => {
                        const { freq, vkar } = {
                            freq: point?.freq || null,
                            vkar: point?.vkar || null,
                        };
                        return {
                            freq,
                            vkar: isNull(vkar) ? null : vkar - (percentageLevel / 100) * vkar,
                        };
                    }),
                };
            }),
        ],
        [categoryAlarm, alarmLevels]
    );

    return (
        <div
            className={classNames(
                'alarm_preview_graph z-modal overflow-hidden rounded-lg bg-white shadow-lg',
                isExpanded && 'origin-bottom-right scale-150'
            )}
        >
            <div className="border-graph-border-grey flex items-start justify-between border border-b-0 bg-white px-5 pb-0 pt-5">
                <div className="flex items-start gap-x-2">
                    <FloatingPanelTitle>{gettext('ALARM_LEVELS')}</FloatingPanelTitle>
                    {loading && <LoadingSpinner />}
                </div>
                <div className="flex gap-x-2">
                    <button
                        type="button"
                        title={gettext('EXPAND_COLLAPSE_GRAPH')}
                        onClick={() => {
                            setIsExpanded((current) => !current);
                        }}
                    >
                        <FontAwesomeIcon icon={faArrows} />
                    </button>
                    {onClose ? (
                        <button type="button" title={gettext('MINIMIZE_GRAPH')} onClick={onClose}>
                            <FontAwesomeIcon icon={faAngleDown} />
                        </button>
                    ) : null}
                </div>
            </div>
            <AlarmLinesGraph alarms={alarms} />
        </div>
    );
}

interface AlarmPreviewToggleButtonProps {
    isActive: boolean;
    onClick: () => void;
}

export function AlarmPreviewToggleButton({
    isActive,
    onClick,
}: AlarmPreviewToggleButtonProps): JSX.Element {
    return (
        <RoundedButton
            type="button"
            variant="big"
            color={isActive ? 'primary' : 'secondary'}
            className="flex h-12 w-12 items-center justify-center px-0 shadow-lg"
            aria-label={gettext('PREVIEW_OF_ALARM_LIMIT')}
            title={gettext('PREVIEW_OF_ALARM_LIMIT')}
            onClick={onClick}
        >
            <FontAwesomeIcon icon={faChartLine} />
        </RoundedButton>
    );
}

export function AlarmPreviewGraphContainer(): JSX.Element {
    const [isVisible, setIsVisible] = useState(true);

    const toggleVisibility = useCallback(() => {
        setIsVisible((current) => !current);
    }, []);

    return (
        <div className="z-modal fixed bottom-24 right-5 flex items-end gap-3">
            {isVisible ? <AlarmPreviewGraphPanel onClose={toggleVisibility} /> : null}
            <div className="flex flex-col gap-3">
                <AlarmPreviewToggleButton isActive={isVisible} onClick={toggleVisibility} />
            </div>
        </div>
    );
}
